import { EverysetUser } from '@lib/types/user.types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export interface AppState {
  user: {
    profile?: EverysetUser;
    IUserInfoDAO?: UserProfile;
    UserAppearance?: UserAppearance;
    InvitesActionType?: InvitesActionTypeDTO;
    FirstAvailActionType?: FirstAvailActionTypeDTO;
    BookedActionType?: BookingsActionTypeDTO;
    allUserIds?: any; // Add the allUserIds field here
    onboardingFirstLoad?: boolean;
    notificationPreferences?: Record<string, boolean>;
    impersonatedUserId?: number;
    tempUser?: any; // User info for users with temp password
  };
  googleMapsApi: {
    loaded: boolean;
  };
}

const initialState: AppState = {
  user: {
    profile: null,
    IUserInfoDAO: null,
    UserAppearance: null,
    InvitesActionType: null,
    FirstAvailActionType: null,
    BookedActionType: null,
    allUserIds: null,
    onboardingFirstLoad: true,
    notificationPreferences: null,
    impersonatedUserId: null,
    tempUser: null,
  },
  googleMapsApi: {
    loaded: false,
  },
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setUserProfile: (state, action: PayloadAction<EverysetUser>) => {
      state.user.profile = action.payload;
    },
    setImpersonatedUserId: (state, action: PayloadAction<number | null>) => {
      state.user.impersonatedUserId = action.payload;
    },
    setUserProfileInfo: (state, action: PayloadAction<UserProfile>) => {
      state.user.IUserInfoDAO = action.payload;
    },
    setUserAppearance: (state, action: PayloadAction<UserAppearance>) => {
      state.user.UserAppearance = action.payload;
    },
    setInvitesActionType: (
      state,
      action: PayloadAction<InvitesActionTypeDTO>
    ) => {
      state.user.InvitesActionType = action.payload;
    },
    setFirstAvailActionType: (
      state,
      action: PayloadAction<FirstAvailActionTypeDTO>
    ) => {
      state.user.FirstAvailActionType = action.payload;
    },
    setBookedActionType: (
      state,
      action: PayloadAction<BookingsActionTypeDTO>
    ) => {
      state.user.BookedActionType = action.payload;
    },
    setAllUserIds: (state, action: PayloadAction<any>) => {
      state.user.allUserIds = action.payload;
    },
    setOnboardingFirstLoad: (state, action) => {
      state.user.onboardingFirstLoad = action.payload;
    },
    setNotificationPreferences: (
      state,
      action: PayloadAction<Record<string, boolean>>
    ) => {
      state.user.notificationPreferences = action.payload;
    },
    setNotificationPreference: (
      state,
      action: PayloadAction<{ notificationType: string; optIn: boolean }>
    ) => {
      const { notificationType, optIn } = action.payload;

      state.user.notificationPreferences = {
        ...(state.user.notificationPreferences ?? {}),
        [notificationType]: optIn,
      };
    },
    setGoogleMapsApiLoaded: (state, action: PayloadAction<boolean>) => {
      state.googleMapsApi.loaded = action.payload;
    },
    completeTutorial: (state, action: PayloadAction<UserTutorialName>) => {
      const userId = state.user.profile?.id;

      if (!userId) return;

      const currentTutorials = state.user.IUserInfoDAO?.user_tutorials ?? [];
      const currentTutorial = currentTutorials.find(
        (tutorial) => tutorial.tutorial_name === action.payload
      );

      if (!currentTutorial) {
        const newTutorials = [
          ...currentTutorials,
          { tutorial_name: action.payload, is_completed: true },
        ];

        state.user.IUserInfoDAO = {
          ...state.user.IUserInfoDAO,
          user_tutorials: newTutorials,
        };

        return;
      }

      const newTutorial = { ...currentTutorial, is_completed: true };
      const newTutorials = currentTutorials.map((tutorial) =>
        tutorial.tutorial_name === action.payload ? newTutorial : tutorial
      );

      state.user.IUserInfoDAO = {
        ...state.user.IUserInfoDAO,
        user_tutorials: newTutorials,
      };
    },
    setTempUserInfo: (state, action: PayloadAction<any>) => {
      state.user.tempUser = action.payload;
    },
    resetStore: () => {
      // Reset the state to its initial value
      return initialState;
    },
  },
});

export const {
  setUserProfile,
  setUserProfileInfo,
  setUserAppearance,
  setInvitesActionType,
  setFirstAvailActionType,
  setBookedActionType,
  setAllUserIds,
  resetStore,
  setOnboardingFirstLoad,
  setNotificationPreferences,
  setNotificationPreference,
  setImpersonatedUserId,
  setGoogleMapsApiLoaded,
  completeTutorial,
  setTempUserInfo,
} = appSlice.actions;

export default appSlice.reducer;
